import { useCallback, useEffect, useMemo, useState } from "react";

import { makeRequest } from "../../utils/request";
import { Badge, Button, Dialog, EmptyState, InputField, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";

type ServicePrincipal = {
  id: string;
  principal: string;
  name: string;
  description: string | null;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
};

type PermissionOption = {
  permission: string;
  group: string;
  description: string;
  internal: boolean;
  deprecated: boolean;
};

type ListResponse = { data: ServicePrincipal[]; permissions: PermissionOption[] };

const EMPTY_FORM = { principal: "", name: "", description: "", permissions: [] as string[] };

/**
 * Platform admin management of service principals — the Workload Identity
 * callers (GSA emails) allowed on service-authenticated API endpoints. They
 * hold permissions from the SAME registry as org service accounts and PATs
 * (including internal machine-only entries), but with no organization
 * binding. Changes take effect without a deploy.
 */
export const AdminServicePrincipalsPage = () => {
  const [principals, setPrincipals] = useState<ServicePrincipal[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<PermissionOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create/edit form state; editingId null = create mode, undefined = closed.
  const [editingId, setEditingId] = useState<string | null | undefined>(undefined);
  // The row being edited, captured at edit-open, so we can diff permissions.
  const [editingOriginal, setEditingOriginal] = useState<ServicePrincipal | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Type-to-confirm modals. Destructive/authorization-changing actions require
  // typing the principal's name — the same friction for revoking access
  // (delete) and for changing what a live caller can do (permission update).
  const [deleteTarget, setDeleteTarget] = useState<ServicePrincipal | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
  const [updateConfirmText, setUpdateConfirmText] = useState("");

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      const response = await makeRequest<ListResponse>("/v1/admin/service-principals");
      setPrincipals(response.data);
      setPermissionOptions(response.permissions);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load service principals");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // Picker groups, "Internal Services" first — the scopes platform services
  // most commonly need — then alphabetical.
  const groupedOptions = useMemo(() => {
    const groups = new Map<string, PermissionOption[]>();
    for (const option of permissionOptions) {
      const list = groups.get(option.group) ?? [];
      list.push(option);
      groups.set(option.group, list);
    }
    return Array.from(groups.entries()).sort(([a], [b]) =>
      a === "Internal Services" ? -1 : b === "Internal Services" ? 1 : a.localeCompare(b),
    );
  }, [permissionOptions]);

  // Permissions the current edit adds/removes vs. the stored row (create → all added).
  const permissionDiff = useMemo(() => {
    const before = editingOriginal?.permissions ?? [];
    const after = form.permissions;
    const added = after.filter((p) => !before.includes(p)).sort();
    const removed = before.filter((p) => !after.includes(p)).sort();
    return { added, removed, changed: added.length > 0 || removed.length > 0 };
  }, [editingOriginal, form.permissions]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditingOriginal(null);
    setFormError(null);
    setOpenGroups({ "Internal Services": true });
    setEditingId(null);
  };

  const openEdit = (row: ServicePrincipal) => {
    setForm({
      principal: row.principal,
      name: row.name,
      description: row.description ?? "",
      permissions: [...row.permissions],
    });
    setEditingOriginal(row);
    setFormError(null);
    // Open every group that has a granted permission so the current state is visible.
    const open: Record<string, boolean> = { "Internal Services": true };
    for (const option of permissionOptions) {
      if (row.permissions.includes(option.permission)) open[option.group] = true;
    }
    setOpenGroups(open);
    setEditingId(row.id);
  };

  const togglePermission = (permission: string) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(permission)
        ? f.permissions.filter((p) => p !== permission)
        : [...f.permissions, permission],
    }));
  };

  // Gate: creating, or editing without a permission change, saves straight
  // through. Editing that adds/removes permissions changes what a live caller
  // can do, so it routes through the type-to-confirm modal first.
  const save = () => {
    setFormError(null);
    if (editingId && permissionDiff.changed) {
      setUpdateConfirmText("");
      setShowUpdateConfirm(true);
      return;
    }
    void doSave();
  };

  const doSave = async () => {
    setIsSaving(true);
    setFormError(null);
    try {
      if (editingId) {
        await makeRequest(`/v1/admin/service-principals/${editingId}`, {
          method: "PATCH",
          body: { name: form.name, description: form.description || undefined, permissions: form.permissions },
        });
      } else {
        await makeRequest("/v1/admin/service-principals", {
          method: "POST",
          body: {
            principal: form.principal.trim(),
            name: form.name.trim(),
            description: form.description || undefined,
            permissions: form.permissions,
          },
        });
      }
      setShowUpdateConfirm(false);
      setEditingId(undefined);
      await load(false);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Failed to save");
      setShowUpdateConfirm(false);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    setError(null);
    try {
      await makeRequest(`/v1/admin/service-principals/${deleteTarget.id}`, { method: "DELETE" });
      setDeleteTarget(null);
      await load(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete");
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <Text className="text-xl font-semibold">Service Principals</Text>
          <Text className="text-sm text-gray-500 dark:text-gray-400">
            Workload Identity callers allowed on service-authenticated API endpoints
          </Text>
        </div>
        <Button
          variant="brand"
          type="button"
          onClick={openCreate}
        >
          Add principal
        </Button>
      </div>

      <div
        className="overflow-y-auto px-6 py-4 space-y-4"
        style={{ height: "calc(100% - 65px)" }}
      >
        {/* Create / edit form */}
        {editingId !== undefined && (
          <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
            <Text className="text-sm font-semibold">{editingId ? "Edit service principal" : "New service principal"}</Text>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputField
                name="principal"
                label="Principal (GSA email)"
                placeholder="my-service-sa@prim-ai-production.iam.gserviceaccount.com"
                value={form.principal}
                isDisabled={!!editingId}
                onChange={(v: string) => setForm((f) => ({ ...f, principal: v }))}
              />
              <InputField
                name="name"
                label="Name"
                placeholder="primvoices-agents"
                value={form.name}
                onChange={(v: string) => setForm((f) => ({ ...f, name: v }))}
              />
            </div>
            <InputField
              name="description"
              label="Description (optional)"
              value={form.description}
              onChange={(v: string) => setForm((f) => ({ ...f, description: v }))}
            />
            <div>
              <div className="flex items-baseline gap-2 mb-2">
                <Text className="text-xs uppercase text-gray-400">Permissions</Text>
                <Text className="text-xs text-gray-400 dark:text-gray-500">
                  {form.permissions.length} selected — same registry as service accounts / PATs, no org binding
                </Text>
              </div>
              <div className="space-y-1 max-h-96 overflow-y-auto border border-gray-100 dark:border-gray-700 rounded p-2">
                {groupedOptions.map(([group, options]) => {
                  const selectedInGroup = options.filter((o: PermissionOption) => form.permissions.includes(o.permission)).length;
                  const isOpen = !!openGroups[group];
                  return (
                    <div key={group}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between py-1.5 px-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded"
                        onClick={() => setOpenGroups((g) => ({ ...g, [group]: !isOpen }))}
                      >
                        <span className="flex items-center gap-2">
                          <Icon
                            name={isOpen ? "expand_more" : "chevron_right"}
                            size={16}
                            className="text-gray-400"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{group}</span>
                        </span>
                        {selectedInGroup > 0 && <Badge variant="info">{selectedInGroup}</Badge>}
                      </button>
                      {isOpen && (
                        <div className="pl-7 pb-2 space-y-1.5">
                          {options.map((option: PermissionOption) => (
                            <label
                              key={option.permission}
                              className="flex items-start gap-2 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={form.permissions.includes(option.permission)}
                                onChange={() => togglePermission(option.permission)}
                              />
                              <span>
                                <span className="text-sm font-mono text-gray-800 dark:text-gray-200">
                                  {option.permission}
                                  {option.internal && (
                                    <span className="ml-1.5 text-[10px] uppercase text-gray-400">internal</span>
                                  )}
                                </span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {formError && <Text className="text-sm text-error">{formError}</Text>}
            <div className="flex gap-2">
              <Button
                variant="brand"
                type="button"
                onClick={save}
                isLoading={isSaving}
                disabled={!form.name || (!editingId && !form.principal)}
              >
                {editingId ? "Save" : "Create"}
              </Button>
              <Button
                variant="ghost"
                type="button"
                onClick={() => setEditingId(undefined)}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <View className="flex items-center justify-center py-20">
            <Spinner className="!text-brand" />
          </View>
        ) : error ? (
          <View className="flex items-center justify-center py-20">
            <Text className="text-error">{error}</Text>
          </View>
        ) : principals.length === 0 ? (
          <EmptyState
            icon="key"
            title="No service principals registered"
            description="Services calling the API with Workload Identity tokens are rejected until their principal is registered here with the permissions they need."
          />
        ) : (
          principals.map((row) => (
            <div
              key={row.id}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Text className="text-sm font-medium">{row.name}</Text>
                  </div>
                  <span
                    className="block text-[11px] font-mono text-gray-400 dark:text-gray-500 truncate"
                    title={row.principal}
                  >
                    {row.principal}
                  </span>
                  {row.description && <Text className="block text-xs text-gray-500 dark:text-gray-400">{row.description}</Text>}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {row.permissions.length === 0 ? (
                      <Badge variant="warning">no permissions</Badge>
                    ) : (
                      row.permissions.map((permission) => (
                        <Badge
                          key={permission}
                          variant="info"
                        >
                          {permission}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => openEdit(row)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => {
                      setDeleteConfirmText("");
                      setDeleteTarget(row);
                    }}
                  >
                    <Icon
                      name="delete"
                      size={16}
                      className="text-error"
                    />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete confirmation — type the principal's name to deregister it. */}
      <Dialog
        isOpen={!!deleteTarget}
        onClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        size="md"
        title="Delete service principal"
        actions={
          <>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              outline
              onClick={confirmDelete}
              disabled={isDeleting || deleteConfirmText.trim() !== deleteTarget?.name}
            >
              {isDeleting ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="delete"
                  size={14}
                />
              )}
              Delete principal
            </Button>
          </>
        }
      >
        {deleteTarget && (
          <View className="p-4 space-y-4">
            <Text className="text-sm text-gray-600 dark:text-gray-300">
              This immediately deregisters{" "}
              <span className="font-mono text-gray-800 dark:text-gray-200">{deleteTarget.principal}</span>. Its Workload
              Identity calls to the API are rejected on the next request — there is no grace period.
            </Text>
            <View>
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                Type <span className="font-mono font-medium text-gray-700 dark:text-gray-200">{deleteTarget.name}</span>{" "}
                to confirm.
              </Text>
              <InputField
                name="deleteConfirm"
                value={deleteConfirmText}
                placeholder={deleteTarget.name}
                onChange={(v: string) => setDeleteConfirmText(v)}
              />
            </View>
          </View>
        )}
      </Dialog>

      {/* Permission-update confirmation — diff + type-to-confirm before changing
          what a live caller can do. */}
      <Dialog
        isOpen={showUpdateConfirm}
        onClose={() => {
          if (!isSaving) setShowUpdateConfirm(false);
        }}
        size="lg"
        title="Update permissions"
        actions={
          <>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => setShowUpdateConfirm(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              outline
              onClick={doSave}
              disabled={isSaving || updateConfirmText.trim() !== editingOriginal?.name}
            >
              {isSaving ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="check"
                  size={14}
                />
              )}
              Apply changes
            </Button>
          </>
        }
      >
        {editingOriginal && (
          <View className="p-4 space-y-4">
            <Text className="text-sm text-gray-600 dark:text-gray-300">
              This changes what{" "}
              <span className="font-mono text-gray-800 dark:text-gray-200">{editingOriginal.principal}</span> can do on
              the API, effective on its next request.
            </Text>

            <View className="space-y-3">
              {permissionDiff.added.length > 0 && (
                <View>
                  <Text className="text-xs uppercase text-gray-400 mb-1.5">Granting ({permissionDiff.added.length})</Text>
                  <div className="flex flex-wrap gap-1.5">
                    {permissionDiff.added.map((permission) => (
                      <span
                        key={permission}
                        className="text-xs font-mono px-2 py-0.5 rounded border border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-300"
                      >
                        + {permission}
                      </span>
                    ))}
                  </div>
                </View>
              )}
              {permissionDiff.removed.length > 0 && (
                <View>
                  <Text className="text-xs uppercase text-gray-400 mb-1.5">Revoking ({permissionDiff.removed.length})</Text>
                  <div className="flex flex-wrap gap-1.5">
                    {permissionDiff.removed.map((permission) => (
                      <span
                        key={permission}
                        className="text-xs font-mono px-2 py-0.5 rounded border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
                      >
                        − {permission}
                      </span>
                    ))}
                  </div>
                </View>
              )}
            </View>

            <View>
              <Text className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">
                Type <span className="font-mono font-medium text-gray-700 dark:text-gray-200">{editingOriginal.name}</span>{" "}
                to confirm.
              </Text>
              <InputField
                name="updateConfirm"
                value={updateConfirmText}
                placeholder={editingOriginal.name}
                onChange={(v: string) => setUpdateConfirmText(v)}
              />
            </View>
          </View>
        )}
      </Dialog>
    </View>
  );
};
